<template>
  <transition name="fade">
    <div v-if="visible" class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black bg-opacity-50 p-4 sm:p-6">
      <div class="relative w-full max-w-md rounded-lg bg-white shadow-xl ring-1 ring-gray-200">
        
        <!-- Header -->
        <div class="flex items-center justify-between border-b px-4 py-3 bg-gray-50 rounded-t-lg">
          <h3 class="text-lg font-medium text-gray-900">
            Gérer les crédits / quotas
          </h3>
          <button @click="close" class="text-gray-400 hover:text-gray-500 focus:outline-none">
            <span class="sr-only">Fermer</span>
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="p-6 space-y-6">
          <div v-if="client">
             <p class="text-sm text-gray-600 mb-4">Client : <span class="font-semibold">{{ client.Nom }}</span> ({{ client.Email_Gologin }})</p>
             
             <!-- Sélection du Genre -->
             <div>
               <label class="block text-sm font-medium text-gray-700 mb-2">Genre du compte cible <span class="text-red-500">*</span></label>
               <div class="flex items-center space-x-4">
                 <div class="flex items-center">
                   <input id="gender-homme" name="gender" type="radio" value="Homme" v-model="form.gender" class="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300">
                   <label for="gender-homme" class="ml-2 block text-sm text-gray-700">Homme</label>
                 </div>
                 <div class="flex items-center">
                   <input id="gender-femme" name="gender" type="radio" value="Femme" v-model="form.gender" class="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300">
                   <label for="gender-femme" class="ml-2 block text-sm text-gray-700">Femme</label>
                 </div>
               </div>
             </div>

             <!-- Sélection de l'action -->
             <div class="border-t pt-4 mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Type d'action</label>
                <div class="flex space-x-2 bg-gray-100 p-1 rounded-md">
                  <button 
                    type="button"
                    class="flex-1 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none"
                    :class="form.action === 'reset' ? 'bg-white shadow text-green-700' : 'text-gray-500 hover:text-gray-700'"
                    @click="form.action = 'reset'"
                  >
                    Réinitialiser Quota
                  </button>
                  <button 
                    type="button"
                    class="flex-1 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none"
                    :class="form.action === 'bonus' ? 'bg-white shadow text-green-700' : 'text-gray-500 hover:text-gray-700'"
                    @click="form.action = 'bonus'"
                  >
                    Ajouter Bonus
                  </button>
                </div>
             </div>

             <!-- Champ Montant (seulement pour Bonus) -->
             <div v-if="form.action === 'bonus'" class="mt-4">
               <label for="amount" class="block text-sm font-medium text-gray-700">Montant à ajouter</label>
               <input 
                 type="number" 
                 id="amount" 
                 v-model.number="form.amount" 
                 class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                 placeholder="Ex: 10"
                 min="1"
               >
             </div>
             
             <!-- Message d'info pour Reset -->
             <div v-if="form.action === 'reset'" class="mt-4 text-sm text-gray-500 bg-blue-50 p-3 rounded border border-blue-100">
               ℹ️ Cette action réinitialisera le quota journalier/mensuel du compte {{ form.gender }} de cet utilisateur.
             </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 border-t bg-gray-50 px-4 py-3 rounded-b-lg">
          <button
            type="button"
            class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            @click="close"
            :disabled="loading"
          >
            Annuler
          </button>
          <button
            type="button"
            class="inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
            :class="[
              loading ? 'opacity-75 cursor-wait' : '',
              form.action === 'reset' ? 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500' : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
            ]"
            @click="submit"
            :disabled="loading"
          >
            <span v-if="loading" class="mr-2">⏳</span>
            {{ form.action === 'reset' ? 'Réinitialiser' : 'Ajouter Crédits' }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from '../../plugins/axios';

export default {
  name: 'CreditsModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    client: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'success'],
  data() {
    return {
      loading: false,
      form: {
        gender: 'Homme', // Défaut
        action: 'bonus', // 'reset' | 'bonus'
        amount: 10
      }
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Reset form on open, but keep gender if previously selected or default
        this.form.action = 'bonus';
        this.form.amount = 10;
        this.loading = false;
      }
    }
  },
  methods: {
    close() {
      if (!this.loading) {
        this.$emit('close');
      }
    },
    async submit() {
      if (!this.client) return;

      this.loading = true;
      try {
        const payload = {
          targetEmail: this.client.Email_Gologin,
          gender: this.form.gender
        };

        let url = '';
        
        if (this.form.action === 'reset') {
          url = '/admin2/quota/reset';
        } else {
          url = '/admin2/quota/bonus/add';
          payload.amount = this.form.amount;
          
          if (!payload.amount || payload.amount <= 0) {
            throw new Error("Veuillez saisir un montant valide.");
          }
        }

        const response = await axios.post(url, payload);
        
        if (response.status === 200 || response.status === 201) {
           const actionText = this.form.action === 'reset' ? 'réinitialisés' : 'ajoutés';
           this.$emit('success', `Succès : Quotas/Crédits ${actionText} pour ${this.form.gender}.`);
           this.close();
        } else {
           throw new Error("Réponse inattendue du serveur.");
        }

      } catch (err) {
        console.error('Erreur crédits :', err);
        // Gestion basique des erreurs axios
        const msg = err.response?.data?.error || err.message || "Une erreur est survenue";
        alert(`Erreur : ${msg}`);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

